import { ActiveFilter } from '@epicuro-next/models/filters';
import {
  IOrdersGroupType,
  OrderFilterId,
  OrderGroup,
} from '@epicuro-next/models/order-book';
import { Dictionary } from '@ngrx/entity';

import { IGroupPipesMap, IOrderGroupLine } from '../../models';
import { getDateValue, getNumberValue } from '../get-value/get-value';

export function mapOrderGroupToGroupRow(
  orderGroup: OrderGroup,
  parentId: string | null,
  groupType: IOrdersGroupType,
  pipes: IGroupPipesMap,
  locale: string,
): IOrderGroupLine {
  return {
    ...orderGroup,
    value: getNumberValue<number>(orderGroup.value, pipes.number),
    displayName:
      groupType === IOrdersGroupType.Month
        ? getDateValue(orderGroup.name, locale, 'MMMM y')
        : orderGroup.name,
    parentId,
    groupType,
    internalId: getGroupInternalId(orderGroup.name, parentId),
  };
}

export function getFlattenedOrderGroupLines(
  orderGroups: OrderGroup[],
  parentId: string | null,
  groupType: IOrdersGroupType,
  pipes: IGroupPipesMap,
  locale: string,
): Record<string, IOrderGroupLine> {
  return orderGroups.reduce((acc, group) => {
    const lineItem = mapOrderGroupToGroupRow(
      group,
      parentId,
      groupType,
      pipes,
      locale,
    );

    acc[lineItem.internalId] = lineItem;

    if (group.nestedItems.length) {
      const nestedGroupType = Object.values(IOrdersGroupType).find(
        (type) => type !== groupType,
      ) as IOrdersGroupType;

      return {
        ...acc,
        ...getFlattenedOrderGroupLines(
          group.nestedItems,
          lineItem.internalId,
          nestedGroupType,
          pipes,
          locale,
        ),
      } as Record<string, IOrderGroupLine>;
    }

    return acc;
  }, {} as Record<string, IOrderGroupLine>);
}

const PARENT_SPLITTER = '__parent-';

export function getGroupInternalId(name: string, parentId: string | null) {
  const parentSuffix = parentId ? `${PARENT_SPLITTER}${parentId}` : '';
  return `${name}${parentSuffix}`;
}

export function getParentChain<
  T extends Dictionary<{
    parentId: string | null;
  }>,
>(entity: T, groupId: string): string[] {
  const parentId = entity[groupId]?.parentId;
  return !parentId ? [] : [parentId, ...getParentChain(entity, parentId)];
}

export function getFiltersForGroups(groups: IOrderGroupLine[]): ActiveFilter[] {
  const groupTypeToFieldsIdsMap = {
    [IOrdersGroupType.Month]: OrderFilterId.Month,
    [IOrdersGroupType.Category]: OrderFilterId.MasterCategory,
  };

  return groups.map(({ groupType, name }) => {
    return {
      id: groupTypeToFieldsIdsMap[groupType],
      values: [name],
    };
  });
}
